import { BellPlus, Check, FolderPlus, Pencil, Plus, RefreshCw, Star, Tag as TagIcon, Trash2, X, Lock, Unlock } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { tagNotificationLink } from '@/features/automations/automationPrefill'
import { usePermission } from '@/hooks/usePermission'
import { useTagCategories } from '@/hooks/useTagCategories'
import { useTags } from '@/hooks/useTags'
import { PERMISSION_KEYS } from '@/stores/permission.store'
import { categoryDisplayName, type Tag, type TagCategory, type TagTargetType } from '@/types/api/tag'
import { cn } from '@/utils/cn'
import { parseApiError } from '@/utils/error-parser'

const PRESET_COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899']
const NONE = '__none__'

const TYPE_COPY: Record<TagTargetType, { title: string; description: string }> = {
  RECORD: { title: 'Record tags', description: 'Classify records in this group.' },
  ACTIVITY: { title: 'Activity tags', description: 'Classify notes added to records. A note carries one tag.' },
  FOLLOWUP: { title: 'Follow-up tags', description: 'Classify follow-ups on records. A follow-up carries one tag.' },
}

interface TagManagerProps {
  recordGroupId: string
  readOnly: boolean
  /** Record tag applied to every new record (RECORD card only). */
  defaultRecordTagId?: string | null
  onDefaultRecordTagChange?: (tagId: string | null) => void
}

/** Tag administration for one record group: one card per tag type, tags grouped by category. */
export function TagManager({ recordGroupId, readOnly, defaultRecordTagId, onDefaultRecordTagChange }: TagManagerProps) {
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <TagTypeCard
        recordGroupId={recordGroupId}
        targetType="RECORD"
        readOnly={readOnly}
        defaultTagId={defaultRecordTagId ?? null}
        onDefaultTagChange={onDefaultRecordTagChange}
      />
      <TagTypeCard recordGroupId={recordGroupId} targetType="ACTIVITY" readOnly={readOnly} />
      <TagTypeCard recordGroupId={recordGroupId} targetType="FOLLOWUP" readOnly={readOnly} />
    </div>
  )
}

interface TagTypeCardProps {
  recordGroupId: string
  targetType: TagTargetType
  readOnly: boolean
  defaultTagId?: string | null
  onDefaultTagChange?: (tagId: string | null) => void
}

function TagTypeCard({ recordGroupId, targetType, readOnly, defaultTagId, onDefaultTagChange }: TagTypeCardProps) {
  const { tags, isLoading, createTag, isCreating, archiveTag, isArchiving, moveTag, setAssignmentMode, isSettingMode } =
    useTags(recordGroupId, targetType)
  const {
    categories, createCategory, isCreating: isCreatingCategory, isUpdating: isUpdatingCategory,
    updateCategory, deleteCategory,
  } = useTagCategories(recordGroupId, targetType)
  // "One tag per category" only matters for records: activities and follow-ups carry one tag each.
  const supportsSingleSelect = targetType === 'RECORD'
  // Automations trigger on record tags, so only those offer "notify when added"
  const { canAnywhere } = usePermission()
  const canNotify = targetType === 'RECORD' && canAnywhere(PERMISSION_KEYS.AUTOMATION_CREATE)

  const [label, setLabel] = useState('')
  const [color, setColor] = useState<string | undefined>()
  const [newTagCategory, setNewTagCategory] = useState<string>(NONE)
  const [showNewCategory, setShowNewCategory] = useState(false)
  const [editing, setEditing] = useState<TagCategory | null>(null)
  const [confirmArchive, setConfirmArchive] = useState<Tag | null>(null)
  const [confirmDeleteCategory, setConfirmDeleteCategory] = useState<TagCategory | null>(null)

  const sections = useMemo(() => {
    const byCategory = new Map<string | null, Tag[]>()
    for (const tag of tags) {
      const key = tag.categoryId && categories.some((c) => c.id === tag.categoryId) ? tag.categoryId : null
      byCategory.set(key, [...(byCategory.get(key) ?? []), tag])
    }
    return [
      ...categories.map((category) => ({ category, tags: byCategory.get(category.id) ?? [] })),
      { category: null, tags: byCategory.get(null) ?? [] },
    ]
  }, [tags, categories])

  const handleCreateTag = () => {
    if (!label.trim()) return
    createTag(
      { label: label.trim(), colorHex: color, categoryId: newTagCategory === NONE ? null : newTagCategory },
      {
        onSuccess: () => { setLabel(''); setColor(undefined); toast.success('Tag created') },
        onError: (err) => toast.error(parseApiError(err).detail || 'Failed to create tag'),
      }
    )
  }

  const copy = TYPE_COPY[targetType]

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <TagIcon className="h-4 w-4 text-neutral-400" />
          {copy.title}
        </CardTitle>
        <CardDescription>{copy.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {onDefaultTagChange && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-primary-100 bg-primary-50/40 px-3 py-2.5">
            <div className="min-w-0">
              <p className="text-sm font-medium text-neutral-800">Default tag for new records</p>
              <p className="text-xs text-neutral-500">Added automatically to every record created in this group, including public form submissions.</p>
            </div>
            <Select
              value={defaultTagId ?? NONE}
              disabled={readOnly}
              onValueChange={(value) => onDefaultTagChange(value === NONE ? null : value)}
            >
              <SelectTrigger className="h-8 w-44 bg-white text-xs">
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>None</SelectItem>
                {tags.map((tag) => (
                  <SelectItem key={tag.id} value={tag.id}>{tag.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {!readOnly && (
          <div className="space-y-2 rounded-xl border border-neutral-100 bg-neutral-50/60 p-3">
            <div className="flex gap-2">
              <input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleCreateTag() }}
                placeholder="Tag name…"
                maxLength={64}
                className="min-w-0 flex-1 rounded-lg border border-neutral-200 bg-white px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
              {categories.length > 0 && (
                <Select value={newTagCategory} onValueChange={setNewTagCategory}>
                  <SelectTrigger className="h-8 w-36 bg-white text-xs" aria-label="Category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>No category</SelectItem>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{categoryDisplayName(c)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              {PRESET_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  aria-label={`Colour ${c}`}
                  onClick={() => setColor(color === c ? undefined : c)}
                  className={cn('h-5 w-5 rounded-full transition-all', color === c && 'ring-2 ring-neutral-400 ring-offset-1')}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleCreateTag}
                disabled={!label.trim() || isCreating}
                className="flex h-7 items-center gap-1.5 rounded-lg bg-primary-600 px-3 text-xs font-semibold text-white transition-colors hover:bg-primary-700 disabled:opacity-40"
              >
                {isCreating ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />} Create tag
              </button>
              <button
                type="button"
                onClick={() => setShowNewCategory(true)}
                className="flex h-7 items-center gap-1.5 rounded-lg border border-neutral-200 bg-white px-3 text-xs font-semibold text-neutral-700 hover:bg-neutral-50"
              >
                <FolderPlus className="h-3 w-3" /> New category
              </button>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-8 rounded-lg" />)}</div>
        ) : tags.length === 0 && categories.length === 0 ? (
          <p className="py-4 text-center text-sm text-neutral-400">No tags yet</p>
        ) : (
          <div className="space-y-4">
            {sections.map(({ category, tags: sectionTags }) => {
              if (!category && sectionTags.length === 0) return null
              return (
                <section key={category?.id ?? 'uncategorized'} className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
                      {category ? categoryDisplayName(category) : categories.length > 0 ? 'Uncategorized' : 'Tags'}
                    </h4>
                    {category?.singleSelect && supportsSingleSelect && (
                      <Badge variant="outline" className="text-[10px]" title="A record can carry only one tag from this category">
                        One per record
                      </Badge>
                    )}
                    {category && !readOnly && (
                      <span className="ml-auto flex items-center gap-1">
                        <button
                          type="button"
                          aria-label="Edit category"
                          onClick={() => setEditing(category)}
                          className="flex h-6 w-6 items-center justify-center rounded text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
                        >
                          <Pencil className="h-3 w-3" />
                        </button>
                        <button
                          type="button"
                          aria-label="Delete category"
                          onClick={() => setConfirmDeleteCategory(category)}
                          className="flex h-6 w-6 items-center justify-center rounded text-neutral-400 hover:bg-rose-50 hover:text-rose-500"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </span>
                    )}
                  </div>
                  {sectionTags.length === 0 ? (
                    <p className="rounded-lg border border-dashed border-neutral-200 px-3 py-2 text-xs text-neutral-400">
                      No tags in this category
                    </p>
                  ) : (
                    sectionTags.map((tag) => (
                      <div key={tag.id} className="flex items-center gap-2 rounded-lg border border-neutral-100 bg-neutral-50/40 px-3 py-2">
                        {tag.colorHex && <span className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: tag.colorHex }} />}
                        <span className="flex-1 truncate text-sm font-medium text-neutral-700">{tag.label}</span>
                        {defaultTagId === tag.id && (
                          <Badge variant="secondary" className="gap-1 text-[10px]">
                            <Star className="h-2.5 w-2.5" /> Default
                          </Badge>
                        )}
                        {readOnly ? (
                          tag.assignmentMode === 'RESTRICTED' && (
                            <Badge variant="outline" className="gap-1 text-[10px]" title="Only people allowed to use this tag can apply it">
                              <Lock className="h-2.5 w-2.5" /> Restricted
                            </Badge>
                          )
                        ) : (
                          <button
                            type="button"
                            aria-pressed={tag.assignmentMode === 'RESTRICTED'}
                            aria-label={tag.assignmentMode === 'RESTRICTED' ? `Open ${tag.label} to everyone who can tag` : `Restrict ${tag.label}`}
                            title={tag.assignmentMode === 'RESTRICTED'
                              ? 'Restricted: only people with "Use restricted tags" can apply it. Click to open it.'
                              : 'Open: anyone who can tag can apply it. Click to restrict it.'}
                            disabled={isSettingMode}
                            onClick={() => setAssignmentMode(
                              { tagId: tag.id, mode: tag.assignmentMode === 'RESTRICTED' ? 'OPEN' : 'RESTRICTED' },
                              { onError: (err) => toast.error(parseApiError(err).detail || 'Failed to change the tag') }
                            )}
                            className={cn(
                              'flex h-6 items-center gap-1 rounded px-1.5 text-[10px] font-medium transition-colors',
                              tag.assignmentMode === 'RESTRICTED'
                                ? 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                                : 'text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700'
                            )}
                          >
                            {tag.assignmentMode === 'RESTRICTED' ? <Lock className="h-3 w-3" /> : <Unlock className="h-3 w-3" />}
                            {tag.assignmentMode === 'RESTRICTED' ? 'Restricted' : 'Open'}
                          </button>
                        )}
                        {!readOnly && categories.length > 0 && (
                          <Select
                            value={tag.categoryId ?? NONE}
                            onValueChange={(value) => moveTag(
                              { tagId: tag.id, categoryId: value === NONE ? null : value },
                              { onError: (err) => toast.error(parseApiError(err).detail || 'Failed to move tag') }
                            )}
                          >
                            <SelectTrigger className="h-6 w-28 bg-white px-2 text-[11px]" aria-label={`Category of ${tag.label}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value={NONE}>No category</SelectItem>
                              {categories.map((c) => (
                                <SelectItem key={c.id} value={c.id}>{categoryDisplayName(c)}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        {canNotify && (
                          <Link
                            to={tagNotificationLink(recordGroupId, tag.id)}
                            title={`Notify people when "${tag.label}" is added to a record`}
                            aria-label={`Set up a notification for ${tag.label}`}
                            className="flex h-5 w-5 items-center justify-center rounded text-neutral-400 transition-colors hover:bg-primary-50 hover:text-primary-600"
                          >
                            <BellPlus className="h-3 w-3" />
                          </Link>
                        )}
                        {!readOnly && (
                          <button
                            type="button"
                            aria-label={`Archive ${tag.label}`}
                            onClick={() => setConfirmArchive(tag)}
                            disabled={isArchiving}
                            className="flex h-5 w-5 items-center justify-center rounded text-neutral-400 transition-colors hover:bg-rose-50 hover:text-rose-500"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    ))
                  )}
                </section>
              )
            })}
          </div>
        )}
      </CardContent>

      <CategoryDialog
        open={showNewCategory || !!editing}
        category={editing}
        supportsSingleSelect={supportsSingleSelect}
        isSaving={isCreatingCategory || isUpdatingCategory}
        onClose={() => { setShowNewCategory(false); setEditing(null) }}
        onSave={(name, singleSelect) => {
          const done = {
            onSuccess: () => { setShowNewCategory(false); setEditing(null) },
            onError: (err: unknown) => toast.error(parseApiError(err).detail || 'Failed to save category'),
          }
          if (editing) updateCategory({ id: editing.id, name, singleSelect }, done)
          else createCategory({ name, singleSelect }, done)
        }}
      />

      <Dialog open={!!confirmArchive} onOpenChange={(open) => { if (!open) setConfirmArchive(null) }}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Archive tag "{confirmArchive?.label}"?</DialogTitle>
            <DialogDescription>
              The tag will be hidden from pickers but will remain visible on existing records and activities that carry it.
              {defaultTagId && confirmArchive?.id === defaultTagId && ' It will no longer be added to new records.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmArchive(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (!confirmArchive) return
                const archived = confirmArchive
                archiveTag(archived.id, {
                  onSuccess: () => {
                    toast.success('Tag archived')
                    if (defaultTagId === archived.id) onDefaultTagChange?.(null)
                    setConfirmArchive(null)
                  },
                  onError: () => toast.error('Failed to archive tag'),
                })
              }}
            >
              Archive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDeleteCategory} onOpenChange={(open) => { if (!open) setConfirmDeleteCategory(null) }}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Delete category "{confirmDeleteCategory ? categoryDisplayName(confirmDeleteCategory) : ''}"?</DialogTitle>
            <DialogDescription>Its tags are kept and become uncategorized.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDeleteCategory(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (!confirmDeleteCategory) return
                deleteCategory(confirmDeleteCategory.id, {
                  onSuccess: () => { toast.success('Category deleted'); setConfirmDeleteCategory(null) },
                  onError: () => toast.error('Failed to delete category'),
                })
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}

interface CategoryDialogProps {
  open: boolean
  category: TagCategory | null
  supportsSingleSelect: boolean
  isSaving: boolean
  onClose: () => void
  onSave: (name: string | null, singleSelect: boolean) => void
}

function CategoryDialog({ open, category, supportsSingleSelect, isSaving, onClose, onSave }: CategoryDialogProps) {
  const [name, setName] = useState('')
  const [singleSelect, setSingleSelect] = useState(false)
  const [initializedFor, setInitializedFor] = useState<string | null>(null)

  // Reset the form whenever the dialog opens for another category (or for a new one).
  const key = open ? category?.id ?? 'new' : null
  if (key !== initializedFor) {
    setInitializedFor(key)
    setName(category?.name ?? '')
    setSingleSelect(category?.singleSelect ?? false)
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>{category ? 'Edit category' : 'New category'}</DialogTitle>
          <DialogDescription>Group related tags together. A name is optional.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Category name (optional), e.g. Priority"
            maxLength={64}
            className="w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
          />
          {supportsSingleSelect && (
            <label className="flex cursor-pointer items-start gap-2">
              <Checkbox checked={singleSelect} onCheckedChange={(v) => setSingleSelect(v === true)} className="mt-0.5" />
              <span className="text-sm">
                <span className="block font-medium text-neutral-800">Only one tag at a time</span>
                <span className="block text-xs text-neutral-500">
                  Picking a tag from this category replaces the one a record already has. Existing records are not changed.
                </span>
              </span>
            </label>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={isSaving} onClick={() => onSave(name.trim() || null, singleSelect)}>
            {isSaving ? <RefreshCw className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Check className="mr-1.5 h-3.5 w-3.5" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
